import type { MouseEvent, ReactNode } from 'react'
import { cx } from './core.tsx'

// ------------------------------------------------------------- Breadcrumbs

export type Crumb = { label: string; href?: string; onClick?: () => void }

export function Breadcrumbs({ items }: { items: readonly Crumb[] }) {
  return (
    <nav className="ob-crumbs" aria-label="Trilha de navegação">
      {items.map((item, index) => {
        const isLast = index === items.length - 1
        return (
          <span key={`${item.label}-${index}`} className="ob-inline">
            {isLast ? (
              <span className="ob-crumbs__current" aria-current="page">
                {item.label}
              </span>
            ) : item.href || item.onClick ? (
              <a href={item.href ?? '#'} onClick={item.onClick}>
                {item.label}
              </a>
            ) : (
              <span>{item.label}</span>
            )}
            {isLast ? null : <i className="fas fa-chevron-right" aria-hidden style={{ fontSize: 8 }} />}
          </span>
        )
      })}
    </nav>
  )
}

// -------------------------------------------------------------------- Tabs

export type TabItem<T extends string = string> = { id: T; label: string; count?: number; icon?: string }

/**
 * Abas com scroll horizontal.
 *
 * Nao quebram linha no mobile mesmo com cinco ou mais itens (spec 6) — o
 * container rola. Quebrar linha empurraria o conteudo da pagina para baixo e
 * mudaria a altura do header a cada aba adicionada.
 */
export function Tabs<T extends string = string>({
  items,
  value,
  onChange,
}: {
  items: readonly TabItem<T>[]
  value: T
  onChange: (value: T) => void
}) {
  return (
    <div className="ob-tabs" role="tablist">
      {items.map((item) => (
        <button
          key={item.id}
          type="button"
          role="tab"
          className="ob-tab"
          aria-selected={item.id === value}
          onClick={() => onChange(item.id)}
        >
          {item.icon ? <i className={item.icon} aria-hidden /> : null} {item.label}
          {item.count !== undefined ? <span className="ob-tab__count">{item.count}</span> : null}
        </button>
      ))}
    </div>
  )
}

// ------------------------------------------------------------------ Wizard

export type WizardStep = { id: string; label: string }

/**
 * Indicador de passos horizontal.
 *
 * A spec usa Wizard horizontal para fluxos de ate cinco passos (demanda, Kanban,
 * automacao). Acima disso o indicador nao cabe no mobile e o fluxo deve virar
 * pagina dedicada.
 */
export function Wizard({
  steps,
  currentIndex,
  onStepClick,
}: {
  steps: readonly WizardStep[]
  currentIndex: number
  onStepClick?: (index: number) => void
}) {
  return (
    <div className="ob-wizard__steps">
      {steps.map((step, index) => (
        <span key={step.id} className="ob-inline ob-grow">
          <button
            type="button"
            className={cx(
              'ob-wizard__step',
              index === currentIndex && 'ob-wizard__step--current',
              index < currentIndex && 'ob-wizard__step--done',
            )}
            style={{ border: 'none', background: 'transparent', cursor: onStepClick ? 'pointer' : 'default' }}
            aria-current={index === currentIndex ? 'step' : undefined}
            // So deixa voltar: pular para um passo a frente burlaria as validacoes.
            disabled={!onStepClick || index > currentIndex}
            onClick={() => onStepClick?.(index)}
          >
            <span className="ob-wizard__bullet">
              {index < currentIndex ? <i className="fas fa-check" aria-hidden /> : index + 1}
            </span>
            {step.label}
          </button>
          {index < steps.length - 1 ? <span className="ob-wizard__line" aria-hidden /> : null}
        </span>
      ))}
    </div>
  )
}

// ----------------------------------------------------------------- Sidebar

export type SidebarItem = {
  id: string
  label: string
  icon: string
  badge?: ReactNode
  active?: boolean
  /** Recebe o evento para que a shell possa dar `preventDefault` em `href`. */
  onClick?: (event: MouseEvent<HTMLElement>) => void
  href?: string
}

export function Sidebar({
  items,
  footer,
  global = true,
}: {
  items: readonly SidebarItem[]
  footer?: ReactNode
  /** `global` esconde a sidebar abaixo de 900px — a shell a reabre em Drawer. */
  global?: boolean
}) {
  return (
    <nav className={cx('ob-sidebar', global && 'ob-sidebar--global')} aria-label="Navegação principal">
      {items.map((item) =>
        item.href ? (
          <a
            key={item.id}
            href={item.href}
            className={cx('ob-sidebar__item', item.active && 'ob-sidebar__item--active')}
            aria-current={item.active ? 'page' : undefined}
            onClick={item.onClick}
          >
            <i className={cx('ob-sidebar__icon', item.icon)} aria-hidden />
            <span className="ob-grow">{item.label}</span>
            {item.badge ? <span className="ob-sidebar__badge">{item.badge}</span> : null}
          </a>
        ) : (
          <button
            key={item.id}
            type="button"
            className={cx('ob-sidebar__item', item.active && 'ob-sidebar__item--active')}
            aria-current={item.active ? 'page' : undefined}
            onClick={item.onClick}
          >
            <i className={cx('ob-sidebar__icon', item.icon)} aria-hidden />
            <span className="ob-grow">{item.label}</span>
            {item.badge ? <span className="ob-sidebar__badge">{item.badge}</span> : null}
          </button>
        ),
      )}
      {footer ? <div style={{ marginTop: 'auto' }}>{footer}</div> : null}
    </nav>
  )
}
